import { Heading, toast } from '@medusajs/ui';

import Button from '@modules/common/components/button';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  ACCOUNT_LAST_DIGITS_LENGTH,
  SETTLEMENT_REQUEST_MIN_AMOUNT,
} from '@lib/constants/payment';
import type { MonthlySettlementInfo } from '@lib/hooks/use-monthly-settlement-info';
import { useCreateSettlementRequest } from '@lib/query/mutations/payment';
import { getApiErrorMessage } from '@lib/util/get-api-error-message';
import Modal from '@modules/common/components/modal';

interface SettlementNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  settlementInfo: MonthlySettlementInfo | null;
}

interface FormValues {
  amount: number;
  account_last_five: string;
  remittance_date: string;
  remark: string;
}

const InputField = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`flex flex-col ${className || ''}`}>{children}</div>;

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`text-black-10 text-base ${className || ''}`}>
    {children}
  </label>
);

export const SettlementNotificationModal = ({
  isOpen,
  onClose,
  settlementInfo,
}: SettlementNotificationModalProps) => {
  const billingPeriod = settlementInfo?.billingPeriod;
  const billingMonthLabel = billingPeriod
    ? `${billingPeriod.year} 年 ${billingPeriod.month} 月份帳單`
    : '';

  const defaultAmount = settlementInfo?.current.remainingFromLastMonth || 0;
  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      amount: defaultAmount > 0 ? defaultAmount : undefined,
      account_last_five: '',
      remittance_date: todayDate,
      remark: '',
    },
  });

  // Modal 開啟時重置表單，確保日期和金額為最新值
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      const amount = settlementInfo?.current.remainingFromLastMonth || 0;
      reset({
        amount: amount > 0 ? amount : undefined,
        account_last_five: '',
        remittance_date: today,
        remark: '',
      });
    }
  }, [isOpen, settlementInfo, reset]);

  const createMutation = useCreateSettlementRequest({
    onSuccess: () => {
      toast.success('繳款通知已送出');
      reset();
      onClose();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, '送出失敗，請稍後再試'));
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = handleSubmit((data) => {
    if (!billingPeriod) return;

    createMutation.mutate({
      amount: data.amount,
      createdByType: 'CUSTOMER',
      bankInfo: {
        account: data.account_last_five,
        date: data.remittance_date,
      },
      reference: {
        type: 'PERIOD',
        period: {
          year: billingPeriod.year,
          month: billingPeriod.month,
        },
      },
      customerRemark: data.remark || undefined,
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      close={handleClose}
      data-testid="settlement-notification-modal"
      panelClassName="max-h-[90vh] max-w-[600px] border-0 bg-white-10"
    >
      <Modal.Title>
        <Heading className="mb-4 text-xl font-bold">繳款通知</Heading>
      </Modal.Title>
      <form onSubmit={onSubmit}>
        <Modal.Body>
          <div className="flex max-h-[60vh] w-full flex-col gap-y-4 overflow-y-auto">
            <InputField>
              <Label className="mb-1">繳款帳單</Label>
              <input
                className="border-ui-border-base bg-ui-bg-field w-full rounded-md border px-3 py-2 text-sm"
                value={billingMonthLabel}
                readOnly
                data-testid="billing-label-input"
              />
            </InputField>
            <InputField>
              <Label className="mb-1">金額*</Label>
              <input
                type="number"
                className="border-ui-border-base bg-ui-bg-field placeholder:text-ui-fg-muted w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                placeholder="請輸入金額"
                min={SETTLEMENT_REQUEST_MIN_AMOUNT}
                data-testid="amount-input"
                {...register('amount', {
                  required: '請輸入金額',
                  min: { value: SETTLEMENT_REQUEST_MIN_AMOUNT, message: `金額不得小於 ${SETTLEMENT_REQUEST_MIN_AMOUNT}` },
                  valueAsNumber: true,
                })}
              />
              {errors.amount && (
                <span className="mt-1 text-xs text-rose-500">{errors.amount.message}</span>
              )}
            </InputField>
            <InputField>
              <Label className="mb-1">帳號末五碼*</Label>
              <input
                className="border-ui-border-base bg-ui-bg-field placeholder:text-ui-fg-muted w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                placeholder="請輸入帳號末五碼"
                inputMode="numeric"
                maxLength={ACCOUNT_LAST_DIGITS_LENGTH}
                data-testid="account-last-five-input"
                {...register('account_last_five', {
                  required: '請輸入帳號末五碼',
                  pattern: {
                    value: new RegExp(`^\\d{${ACCOUNT_LAST_DIGITS_LENGTH}}$`),
                    message: `請輸入${ACCOUNT_LAST_DIGITS_LENGTH}位數字`,
                  },
                })}
              />
              {errors.account_last_five && (
                <span className="mt-1 text-xs text-rose-500">{errors.account_last_five.message}</span>
              )}
            </InputField>
            <InputField>
              <Label className="mb-1">匯款日期*</Label>
              <input
                type="date"
                className="border-ui-border-base bg-ui-bg-field placeholder:text-ui-fg-muted w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                data-testid="remittance-date-input"
                {...register('remittance_date', { required: '請選擇匯款日期' })}
              />
              {errors.remittance_date && (
                <span className="mt-1 text-xs text-rose-500">{errors.remittance_date.message}</span>
              )}
            </InputField>
            <InputField>
              <Label className="mb-1">備註</Label>
              <textarea
                placeholder="請輸入備註（選填）"
                rows={3}
                className="border-ui-border-base bg-ui-bg-field placeholder:text-ui-fg-muted w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                data-testid="remark-input"
                {...register('remark')}
              />
            </InputField>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="mt-6 flex gap-3">
            <Button
              type="button"
              variant="outlined"
              onClick={handleClose}
              data-testid="cancel-button"
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              data-testid="submit-button"
            >
              {createMutation.isPending ? '送出中...' : '送出'}
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
